import { useEffect, useMemo, useRef, useState } from "react";
import { api, type RedlineJobFile, type RedlineJobStatus } from "../lib/api";

/**
 * Standalone Redline Creator: batch-compare ad-hoc Word documents with no
 * deal involved. Upload the original set and the revised set, confirm
 * pairings, and a background job generates tracked-changes redlines —
 * downloadable per-file or as a zip. Nothing is kept: files expire from the
 * server's temp area about two hours after upload.
 */

interface LocalFile {
  key: string;
  filename: string;
  state: "uploading" | "ready" | "error";
  message?: string;
  ready?: RedlineJobFile;
}

const MAX_PAIRS = 100;

export default function RedlineCreatorPage() {
  const [ours, setOurs] = useState<LocalFile[]>([]);
  const [theirs, setTheirs] = useState<LocalFile[]>([]);
  const [author, setAuthor] = useState("");
  const [proposal, setProposal] = useState<{
    pairs: { oursId: string; theirsId: string; score: number }[];
  } | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [manualPairs, setManualPairs] = useState<{ oursId: string; theirsId: string }[]>([]);
  const [manualOurs, setManualOurs] = useState("");
  const [manualTheirs, setManualTheirs] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [job, setJob] = useState<RedlineJobStatus | null>(null);
  const [zipBusy, setZipBusy] = useState(false);

  const oursReady = useMemo(() => ours.filter((f) => f.ready).map((f) => f.ready!), [ours]);
  const theirsReady = useMemo(() => theirs.filter((f) => f.ready).map((f) => f.ready!), [theirs]);
  const byFileId = useMemo(
    () => new Map([...oursReady, ...theirsReady].map((f) => [f.fileId, f])),
    [oursReady, theirsReady],
  );

  const pairKey = (o: string, t: string) => `${o}::${t}`;

  const confirmedPairs = useMemo(() => {
    const auto = (proposal?.pairs ?? [])
      .filter((p) => checked[pairKey(p.oursId, p.theirsId)])
      .map((p) => ({ oursId: p.oursId, theirsId: p.theirsId }));
    return [...auto, ...manualPairs];
  }, [proposal, checked, manualPairs]);

  const usedOurs = new Set(confirmedPairs.map((p) => p.oursId));
  const usedTheirs = new Set(confirmedPairs.map((p) => p.theirsId));
  const freeOurs = oursReady.filter((f) => !usedOurs.has(f.fileId));
  const freeTheirs = theirsReady.filter((f) => !usedTheirs.has(f.fileId));

  // Poll job progress while running.
  useEffect(() => {
    if (!job || job.state !== "running") return;
    // clearInterval can't cancel an in-flight request — guard the late
    // setJob so a "Start over" mid-request can't resurrect the job view.
    let stopped = false;
    const t = setInterval(async () => {
      try {
        const status = await api.getRedlineJob(job.id);
        if (!stopped) setJob(status);
      } catch (e) {
        if (stopped) return;
        const msg = e instanceof Error ? e.message : String(e);
        // Only a 404 means the job is truly gone (expired / server
        // restarted). Anything else is a transient blip — a long batch
        // must survive one dropped poll, so just try again next tick.
        if (msg.startsWith("404")) {
          clearInterval(t);
          setErr(
            "Lost track of the job — the server may have restarted mid-run. Start the batch again.",
          );
          setJob(null);
        }
      }
    }, 1500);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [job?.id, job?.state]);

  async function addFiles(side: "ours" | "theirs", files: File[]) {
    const setter = side === "ours" ? setOurs : setTheirs;
    // Assign each File its key up front and update by key — matching by
    // filename corrupts state when two same-named files are added.
    const uploads = files
      .filter((f) => /\.docx$/i.test(f.name))
      .map((f) => ({ file: f, key: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 8)}` }));
    const skipped = files.filter((f) => !/\.docx$/i.test(f.name));
    setter((prev) => [
      ...prev,
      ...uploads.map(({ file, key }) => ({
        key,
        filename: file.name,
        state: "uploading" as const,
      })),
      ...skipped.map((f) => ({
        key: `${f.name}-skip-${Math.random().toString(36).slice(2, 8)}`,
        filename: f.name,
        state: "error" as const,
        message: "Only .docx files can be compared.",
      })),
    ]);
    setProposal(null);
    setManualPairs([]);
    await Promise.all(
      uploads.map(async ({ file, key }) => {
        try {
          const ready = await api.uploadRedlineJobFile(file);
          setter((prev) => prev.map((p) => (p.key === key ? { ...p, state: "ready", ready } : p)));
        } catch (e) {
          const message = e instanceof Error ? e.message : String(e);
          setter((prev) => prev.map((p) => (p.key === key ? { ...p, state: "error", message } : p)));
        }
      }),
    );
  }

  async function match() {
    setBusy(true);
    setErr(null);
    setManualPairs([]);
    try {
      const p = await api.proposeRedlineJobPairs(
        oursReady.map((f) => f.token),
        theirsReady.map((f) => f.token),
      );
      setProposal(p);
      setChecked(Object.fromEntries(p.pairs.map((x) => [pairKey(x.oursId, x.theirsId), true])));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function start() {
    setBusy(true);
    setErr(null);
    try {
      const tokenOf = (fileId: string) => byFileId.get(fileId)!.token;
      const { jobId } = await api.createRedlineJob({
        ...(author.trim() ? { author: author.trim() } : {}),
        pairs: confirmedPairs.map((p) => ({
          oursToken: tokenOf(p.oursId),
          theirsToken: tokenOf(p.theirsId),
        })),
      });
      setJob(await api.getRedlineJob(jobId));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function downloadZip() {
    if (!job) return;
    setZipBusy(true);
    try {
      const blob = await api.downloadRedlineJobZip(job.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "redlines.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setZipBusy(false);
    }
  }

  function reset() {
    // "Start over" is the one deliberate discard path — tell the server to
    // drop the finished job's outputs rather than waiting for the sweeper.
    if (job && job.state !== "running") {
      void api.cancelRedlineJob(job.id, { discard: true }).catch(() => undefined);
    }
    setOurs([]);
    setTheirs([]);
    setProposal(null);
    setChecked({});
    setManualPairs([]);
    setJob(null);
    setErr(null);
  }

  const filenameOf = (fileId: string) => byFileId.get(fileId)?.filename ?? fileId;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-semibold mb-1">Redline Creator</h1>
      <p className="text-sm text-slate-600 mb-6">
        Compare a batch of Word documents and generate real tracked-changes redlines — like
        Word's Compare Documents, for up to {MAX_PAIRS} document pairs at once. Files are
        processed in a temporary area and deleted automatically after ~2 hours; nothing is
        saved to a deal.
      </p>

      {job ? (
        <JobView
          job={job}
          zipBusy={zipBusy}
          onDownloadZip={downloadZip}
          onCancel={async () => {
            // Cancel-only: if the job actually finished while we thought it
            // was running, the server ignores this and outputs are kept.
            await api.cancelRedlineJob(job.id).catch(() => undefined);
            const refreshed = await api.getRedlineJob(job.id).catch(() => null);
            if (refreshed) setJob(refreshed);
          }}
          onReset={reset}
          onError={setErr}
          err={err}
        />
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <DropZone
              title="Original documents"
              files={ours}
              onFiles={(f) => addFiles("ours", f)}
              onRemove={(key) => {
                setOurs((prev) => prev.filter((p) => p.key !== key));
                setProposal(null);
                setManualPairs([]);
              }}
            />
            <DropZone
              title="Revised documents"
              files={theirs}
              onFiles={(f) => addFiles("theirs", f)}
              onRemove={(key) => {
                setTheirs((prev) => prev.filter((p) => p.key !== key));
                setProposal(null);
                setManualPairs([]);
              }}
            />
          </div>

          <label className="block text-sm font-medium mb-1">Author for the tracked changes</label>
          <input
            className="input mb-4 max-w-md"
            maxLength={80}
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder="Defaults to your name"
          />

          {proposal === null ? (
            <div>
              <button
                className="btn-primary"
                disabled={busy || oursReady.length === 0 || theirsReady.length === 0}
                onClick={match}
              >
                {busy ? "Matching…" : "Match documents"}
              </button>
              {err && <div className="text-sm text-red-600 mt-3">{err}</div>}
            </div>
          ) : (
            <div className="card p-4">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
                Confirm pairings
              </div>
              {proposal.pairs.length === 0 && manualPairs.length === 0 && (
                <div className="text-sm text-slate-500 mb-2">
                  No filenames matched automatically. Pair documents manually below.
                </div>
              )}
              <ul className="space-y-1 mb-3">
                {proposal.pairs.map((p) => (
                  <li key={pairKey(p.oursId, p.theirsId)} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!checked[pairKey(p.oursId, p.theirsId)]}
                      onChange={(e) =>
                        setChecked((prev) => ({
                          ...prev,
                          [pairKey(p.oursId, p.theirsId)]: e.target.checked,
                        }))
                      }
                    />
                    <span className="truncate">{filenameOf(p.oursId)}</span>
                    <span className="text-slate-400 shrink-0">→</span>
                    <span className="truncate">{filenameOf(p.theirsId)}</span>
                  </li>
                ))}
                {manualPairs.map((p, i) => (
                  <li key={pairKey(p.oursId, p.theirsId)} className="flex items-center gap-2 text-sm">
                    <button
                      className="text-slate-400 hover:text-red-600"
                      title="Remove pair"
                      onClick={() => setManualPairs((prev) => prev.filter((_, j) => j !== i))}
                    >
                      ×
                    </button>
                    <span className="truncate">{filenameOf(p.oursId)}</span>
                    <span className="text-slate-400 shrink-0">→</span>
                    <span className="truncate">{filenameOf(p.theirsId)}</span>
                  </li>
                ))}
              </ul>
              {freeOurs.length > 0 && freeTheirs.length > 0 && (
                <div className="flex items-center gap-2 mb-3 text-sm">
                  <select className="input flex-1" value={manualOurs} onChange={(e) => setManualOurs(e.target.value)}>
                    <option value="">Unmatched original…</option>
                    {freeOurs.map((f) => (
                      <option key={f.fileId} value={f.fileId}>
                        {f.filename}
                      </option>
                    ))}
                  </select>
                  <span className="text-slate-400">→</span>
                  <select
                    className="input flex-1"
                    value={manualTheirs}
                    onChange={(e) => setManualTheirs(e.target.value)}
                  >
                    <option value="">Unmatched revised…</option>
                    {freeTheirs.map((f) => (
                      <option key={f.fileId} value={f.fileId}>
                        {f.filename}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn-secondary text-sm shrink-0"
                    disabled={!manualOurs || !manualTheirs}
                    onClick={() => {
                      setManualPairs((prev) => [...prev, { oursId: manualOurs, theirsId: manualTheirs }]);
                      setManualOurs("");
                      setManualTheirs("");
                    }}
                  >
                    + Pair
                  </button>
                </div>
              )}
              {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
              <div className="flex justify-end gap-2">
                <button className="btn-secondary" onClick={() => setProposal(null)}>
                  Back
                </button>
                <button
                  className="btn-primary"
                  disabled={busy || confirmedPairs.length === 0 || confirmedPairs.length > MAX_PAIRS}
                  onClick={start}
                >
                  {busy
                    ? "Starting…"
                    : `Create ${confirmedPairs.length} redline${confirmedPairs.length === 1 ? "" : "s"}`}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DropZone({
  title,
  files,
  onFiles,
  onRemove,
}: {
  title: string;
  files: LocalFile[];
  onFiles: (files: File[]) => void;
  onRemove: (key: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      className={`card p-4 ${over ? "ring-2 ring-blue-400" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        onFiles(Array.from(e.dataTransfer.files));
      }}
    >
      <div className="font-medium text-sm mb-2">{title}</div>
      <button className="btn-secondary text-sm mb-2" onClick={() => inputRef.current?.click()}>
        + Add .docx files
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".docx"
        multiple
        className="hidden"
        onChange={(e) => {
          onFiles(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
      <div className="text-xs text-slate-400 mb-2">…or drag files here</div>
      <ul className="space-y-1 text-sm">
        {files.map((f) => (
          <li key={f.key} className="flex items-center gap-2">
            {f.state === "uploading" && <span className="text-slate-400 shrink-0">↑</span>}
            {f.state === "ready" && <span className="text-green-600 shrink-0">✓</span>}
            {f.state === "error" && <span className="text-red-600 shrink-0">⚠</span>}
            <span className={`truncate ${f.state === "error" ? "text-red-600" : ""}`} title={f.message}>
              {f.filename}
              {f.message ? ` — ${f.message}` : ""}
            </span>
            <button
              className="ml-auto text-slate-400 hover:text-red-600 shrink-0"
              onClick={() => onRemove(f.key)}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function JobView({
  job,
  zipBusy,
  onDownloadZip,
  onCancel,
  onReset,
  onError,
  err,
}: {
  job: RedlineJobStatus;
  zipBusy: boolean;
  onDownloadZip: () => void;
  onCancel: () => void;
  onReset: () => void;
  onError: (msg: string) => void;
  err: string | null;
}) {
  const pct = job.total === 0 ? 0 : Math.round((job.completed / job.total) * 100);
  const outputs = job.results.filter((r) => r.hasOutput);
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium">
          {job.state === "running"
            ? `Comparing… ${job.completed} / ${job.total}`
            : job.state === "canceled"
              ? `Canceled — ${job.completed} of ${job.total} finished`
              : `Done — ${job.total} pair${job.total === 1 ? "" : "s"} compared`}
        </div>
        {job.state === "running" && (
          <button className="btn-secondary text-sm" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
      <div className="h-2 rounded bg-slate-100 overflow-hidden mb-4">
        <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
      </div>

      <ul className="space-y-1 text-sm mb-4">
        {job.results.map((r) => (
          <li key={r.index} className="flex items-center gap-2">
            {r.ok ? <span className="text-green-600">✓</span> : <span className="text-red-600">⚠</span>}
            <span className="truncate">{r.outputFilename ?? r.theirsFilename}</span>
            <span className="text-xs text-slate-400 ml-auto shrink-0 flex items-center gap-2">
              {r.ok
                ? r.hasOutput
                  ? `${r.revisionCount} revision${r.revisionCount === 1 ? "" : "s"}`
                  : "identical — no redline needed"
                : (r.error ?? "failed")}
              {r.hasOutput && (
                <button
                  className="text-blue-600 hover:underline"
                  onClick={async () => {
                    try {
                      const { url } = await api.redlineJobFileUrl(job.id, r.index);
                      window.open(url, "_blank", "noopener");
                    } catch (e) {
                      onError(
                        e instanceof Error && e.message.startsWith("404")
                          ? "This file has expired from the server (files are kept ~2 hours)."
                          : e instanceof Error
                            ? e.message
                            : String(e),
                      );
                    }
                  }}
                >
                  download
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>

      {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
      <div className="flex justify-end gap-2">
        <button className="btn-secondary" onClick={onReset}>
          Start over
        </button>
        {job.state !== "running" && outputs.length > 0 && (
          <button className="btn-primary" disabled={zipBusy} onClick={onDownloadZip}>
            {zipBusy ? "Preparing…" : `Download all (${outputs.length}) as zip`}
          </button>
        )}
      </div>
      {job.state !== "running" && (
        <div className="text-xs text-slate-400 mt-3">
          Generated files are deleted from the server about two hours after the run — download
          what you need now.
        </div>
      )}
    </div>
  );
}
